import { useState } from 'react';
import { Lock, Share2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import ShareBadgeDialog from '../forms/ShareBadgeDialog';
import InitiateBadge from '/badges/initiate.png';
import RookieBadge from '/badges/rookie.png';
import SpecialistBadge from '/badges/specialist.png';
import ProBadge from '/badges/pro.png';
import MasterBadge from '/badges/master.png';
import GrandmasterBadge from '/badges/grandmaster.png';
import LegendBadge from '/badges/legend.png';
import { Badge } from '../ui/badge';

const BadgeCard = ({ title, totalItems, completedItems, status, badgeLevel, type, sharedBy }) => {
    const progress = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

    const shareUrl =
        typeof window !== 'undefined'
            ? `${window.location.origin}/shared/badge?name=${encodeURIComponent(
                  sharedBy || 'Someone'
              )}&title=${encodeURIComponent(title)}&badgeLevel=${encodeURIComponent(
                  badgeLevel
              )}&type=${encodeURIComponent(type)}&totalItems=${totalItems}&completedItems=${completedItems}`
            : '';

    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const handleShare = () => setShareDialogOpen(true);

    const badgeImages = {
        Initiate: InitiateBadge,
        Rookie: RookieBadge,
        Specialist: SpecialistBadge,
        Pro: ProBadge,
        Master: MasterBadge,
        Grandmaster: GrandmasterBadge,
        Legend: LegendBadge,
    };

    const badgeImage = badgeImages[badgeLevel];

    const getDescription = () => {
        if (type === 'project-quality') {
            return `Get >85% score in ${totalItems} project${totalItems > 1 ? 's' : ''}`;
        }
        if (type === 'quiz-perfectionist') {
            return `Get >90% score in ${totalItems} quiz${totalItems > 1 ? 'zes' : ''}`;
        }
        if (type === 'lesson' || type === 'project' || type === 'quiz') {
            return `Complete ${totalItems} ${type}s`;
        }
        if (type === 'streak-consistency') {
            return `complete ${totalItems} days in a row`;
        }
        if (type === 'level') {
            return `Reach level ${totalItems}`;
        }

        return `Complete ${totalItems} items`;
    };

    // Completed Badge
    if (status === 'completed') {
        return (
            <Card className="w-52.5 h-66.25 border border-neutral-300 bg-transparent">
                <CardContent className="flex flex-col items-center py-5">
                    <img src={badgeImage} alt={`${title} Badge`} className="w-20 h-20 mb-3" />
                    <div className="flex flex-col items-center text-center">
                        <h5 className="mb-1 font-semibold h-12 line-clamp-2">{title}</h5>
                        <p className="mb-3 text-sm line-clamp-2 h-9">{getDescription()}</p>
                        <div className="w-full">
                            <Button onClick={handleShare} className="w-full" variant="ghost">
                                Share Badge <Share2 className="w-4 h-4" />
                            </Button>
                            <ShareBadgeDialog
                                open={shareDialogOpen}
                                onOpenChange={setShareDialogOpen}
                                shareUrl={shareUrl}
                                title={title}
                                description={getDescription()}
                                name={sharedBy || 'Someone'}
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }
    // In Progress Badge
    if (status === 'in-progress') {
        return (
            <Card className="w-52.5 h-66.25 border border-neutral-300 bg-transparent">
                <CardContent className="flex flex-col items-center py-5">
                    <img src={badgeImage} alt={`${title} Badge`} className="w-20 h-20 mb-3" />
                    <div className="text-center flex flex-col items-center">
                        <h5 className="mb-1 font-semibold h-12 line-clamp-2">{title}</h5>
                        <p className="mb-3 text-sm line-clamp-2 h-9">{getDescription()}</p>
                        <div className="w-full">
                            <Progress value={progress} />
                            <div className="flex justify-center items-center text-xs mt-2">
                                <span className="text-neutral-500">Earn +50 XP</span>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }
    // Locked Badge
    return (
        <Card className="w-52.5 h-66.25 border border-neutral-300 opacity-60">
            <CardContent className="flex flex-col items-center py-5">
                <div className="w-20 h-20 bg-neutral-300 rounded-full flex items-center justify-center mb-3">
                    <Lock className="w-10 h-10 text-neutral-700" strokeWidth={2} />
                </div>
                <div className="text-center flex flex-col items-center">
                    <h5 className="text-neutral-700 mb-1 font-semibold text-center h-12 line-clamp-2">
                        {title}
                    </h5>
                    <p className="text-sm text-neutral-500 mb-3 line-clamp-2 h-10">
                        {getDescription()}
                    </p>
                    <Badge variant={'destructive'} className="bg-neutral-500 text-white capitalize">
                        LEARNING
                    </Badge>
                </div>
            </CardContent>
        </Card>
    );
};

export default BadgeCard;
